import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { formatDate, formatMoney } from '../shared/formatting';
import { StackedAreaChart } from '../shared/charts';

type Locale = 'hr' | 'en';

type CashRow = {
    id: number;
    supplier_name: string;
    commitment_eur: number;
    expected_sales_eur: number | null;
    expected_cost_eur: number | null;
    expected_ruc_eur: number | null;
    actual_supplier_cost_eur: number | null;
    actual_supplier_invoice_ref: string | null;
    actual_supplier_invoice_date: string | null;
    committed_at: string;
    due_date: string;
    paid_at: string | null;
    paid_eur: number | null;
    status: string;
    order_reference: string | null;
    notes: string | null;
    is_overdue: boolean;
    days_to_due: number | null;
    pdf_sent_at: string | null;
    pdf_sent_to: string | null;
};

type CashSupplierSummary = {
    supplier_name: string;
    open_eur: number;
    open_count: number;
    credit_limit_eur: number | null;
    usage_pct: number | null;
    default_days_to_pay: number;
};

type CashResponse = {
    rows: CashRow[];
    summary: {
        by_supplier: CashSupplierSummary[];
        total_open_eur: number;
        due_in_14_days_eur: number;
        overdue_eur: number;
    };
};

type ForecastDay = {
    date: string;
    inflows_certain_eur: number;
    inflows_expected_eur: number;
    outflows_scheduled_eur: number;
    opening_balance_eur: number;
    ending_balance_eur: number;
    alert: 'negative' | 'low' | null;
};

type ForecastResponse = {
    today: string;
    horizon_days: number;
    starting_balance_eur: number;
    starting_balance_as_of: string | null;
    days: ForecastDay[];
    totals: {
        inflows_certain_eur: number;
        inflows_expected_eur: number;
        outflows_scheduled_eur: number;
        lowest_balance_eur: number;
        lowest_balance_date: string | null;
    };
    alerts: Array<{ date: string; kind: string; message: string; balance_eur: number }>;
};

/**
 * Cash Schedule pregled — sigurni odljevi prema dobavljačima zlata (Gold CM Planer commits).
 * Iskorištenost kreditnih limita po dobavljaču, dospijeća, plaćanja.
 */
export function CashflowScheduleTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<CashResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<'open' | 'all' | 'paid'>('open');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setData(await api<CashResponse>(`/gold-cm-planner/cash-schedule?status=${statusFilter}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => { void load(); }, [load]);

    async function markPaid(id: number, defaultEur: number) {
        const amount = window.prompt(t(`Iznos koji je plaćen (default ${defaultEur}):`, `Amount paid (default ${defaultEur}):`), String(defaultEur));
        if (amount === null) return;
        const paidEur = parseFloat(amount);
        if (!Number.isFinite(paidEur) || paidEur < 0) {
            alert(t('Neispravan iznos.', 'Invalid amount.'));
            return;
        }
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${id}/mark-paid`, token, 'POST', { paid_eur: paidEur });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function cancel(id: number, ref: string | null) {
        if (!confirm(t(`Stornirati narudžbu${ref ? ' (' + ref + ')' : ''}? Bit će obrisana iz cash schedule.`, `Cancel order${ref ? ' (' + ref + ')' : ''}? It will be removed from cash schedule.`))) return;
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${id}`, token, 'DELETE');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function sendPdf(id: number, supplierName: string) {
        if (!confirm(t(`Poslati PDF nalog narudžbe dobavljaču ${supplierName}?`, `Send PDF order to supplier ${supplierName}?`))) return;
        try {
            const r = await mutate<{ ok: boolean; sent_to: string }>(`/gold-cm-planner/cash-schedule/${id}/send-pdf`, token, 'POST', {});
            alert(t(`✓ PDF poslan na ${r.sent_to}`, `✓ PDF sent to ${r.sent_to}`));
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function markProcessed(id: number) {
        const inform = confirm(t('Narudžba obrađena (fiksirano). Informirati hedging (otpuštanje / provjera)?', 'Order processed (fixed). Inform hedging (release / check)?'));
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${id}/processed`, token, 'POST', { hedge_inform: inform });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function downloadFile(id: number, kind: 'pdf' | 'xlsx', supplierName: string) {
        try {
            const res = await fetch(`${API_BASE}/gold-cm-planner/cash-schedule/${id}/download.${kind}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `narudzba-CS${id}-${supplierName.replace(/[^A-Za-z0-9]/g, '_')}.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Download fail');
        }
    }

    async function setActualCost(row: CashRow) {
        const def = row.actual_supplier_cost_eur ?? row.expected_cost_eur ?? row.commitment_eur;
        const amount = window.prompt(t(`Stvarna nabavna cijena (EUR) iz dobavljačeve ponude/računa za ${row.supplier_name}:`, `Actual supplier cost (EUR) from supplier offer/invoice for ${row.supplier_name}:`), String(def));
        if (amount === null) return;
        const actual = parseFloat(amount);
        if (!Number.isFinite(actual) || actual < 0) {
            alert(t('Neispravan iznos.', 'Invalid amount.'));
            return;
        }
        const ref = window.prompt(t('Broj ponude/računa dobavljača (opcionalno):', 'Supplier offer/invoice number (optional):'), row.actual_supplier_invoice_ref ?? '');
        if (ref === null) return;
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${row.id}/actual-cost`, token, 'PATCH', {
                actual_supplier_cost_eur: actual,
                actual_supplier_invoice_ref: ref || null,
                actual_supplier_invoice_date: new Date().toISOString().slice(0, 10),
            });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0 0 12px' }}>
                {t(
                    'Sigurni odljevi prema dobavljačima zlata (Gold Category Manager — Planer u m.40.1). Cash dolazi iz ADO firme. Cash schedule odvaja narudžbu (commitment, danas) od plaćanja (na dospijeću).',
                    'Certain outflows to gold suppliers (Gold Category Manager — Planner in m.40.1). Cash is paid by the ADO company. Cash schedule separates ordering (commitment, today) from payment (at maturity).',
                )}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 16 }}>
                <SummaryMetric label={t('Ukupno otvoreno', 'Total open')} value={formatMoney(data.summary.total_open_eur, locale)} accent={data.summary.total_open_eur > 0 ? 'var(--ad-ink)' : '#888'} />
                <SummaryMetric label={t('Dospijeće 14 dana', 'Due within 14 days')} value={formatMoney(data.summary.due_in_14_days_eur, locale)} accent="var(--ad-acc-3)" />
                <SummaryMetric label={t('Past due (overdue)', 'Overdue')} value={formatMoney(data.summary.overdue_eur, locale)} accent={data.summary.overdue_eur > 0 ? '#b14040' : '#888'} />
            </div>

            <h3 style={{ margin: '12px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                {t('Iskorištenost kreditnih limita', 'Credit limit usage')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginBottom: 16 }}>
                {data.summary.by_supplier.map((s) => (
                    <div key={s.supplier_name} style={{
                        background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <strong style={{ color: 'var(--ad-ink)', fontSize: '0.92rem' }}>🏭 {s.supplier_name}</strong>
                            <span className="muted" style={{ fontSize: '0.78rem' }}>{s.open_count} {t('aktivnih', 'open')}</span>
                        </div>
                        <div style={{ marginTop: 6, fontSize: '0.82rem' }}>
                            <strong>{formatMoney(s.open_eur, locale)}</strong>
                            {s.credit_limit_eur != null ? <> / {formatMoney(s.credit_limit_eur, locale)}</> : <span className="muted"> · {t('bez limita', 'no limit')}</span>}
                        </div>
                        {s.credit_limit_eur != null && s.usage_pct != null ? (
                            <div style={{ marginTop: 6, height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{
                                    width: `${Math.min(100, s.usage_pct)}%`,
                                    height: '100%',
                                    background: s.usage_pct > 90 ? '#b14040' : (s.usage_pct > 70 ? 'var(--ad-acc)' : '#1f7a36'),
                                }} />
                            </div>
                        ) : null}
                        {s.credit_limit_eur != null && s.usage_pct != null ? (
                            <div className="muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>{s.usage_pct.toFixed(1)}% {t('iskorišteno', 'used')} · {t('rok', 'pay')} {s.default_days_to_pay}d</div>
                        ) : null}
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {(['open', 'paid', 'all'] as const).map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setStatusFilter(s)}
                        style={{
                            padding: '4px 10px', borderRadius: 6,
                            border: statusFilter === s ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            background: statusFilter === s ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                            color: statusFilter === s ? '#fffbeb' : 'var(--ad-ink)',
                            fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        {s === 'open' ? t('Otvoreno', 'Open') : s === 'paid' ? t('Plaćeno', 'Paid') : t('Sve', 'All')}
                    </button>
                ))}
            </div>

            {data.rows.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Nema obveza s ovim statusom.', 'No entries with this status.')}
                </p>
            ) : (
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Dospijeće', 'Due')}</th>
                                <th>{t('Dobavljač', 'Supplier')}</th>
                                <th>{t('Referenca', 'Reference')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Prodajno', 'Sales')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Nabavno est.', 'Cost est.')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Stvarna nabavna', 'Actual cost')}</th>
                                <th style={{ textAlign: 'right' }}>{t('RUC', 'GM')}</th>
                                <th>{t('Status', 'Status')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => {
                                const sales = r.expected_sales_eur;
                                const costEst = r.expected_cost_eur ?? r.commitment_eur;
                                const actual = r.actual_supplier_cost_eur;
                                const realizedRuc = sales != null && actual != null ? sales - actual : null;
                                const expectedRuc = r.expected_ruc_eur ?? (sales != null ? sales - costEst : null);
                                const ruc = realizedRuc ?? expectedRuc;
                                const rucColor = ruc == null ? '#888' : ruc >= 0 ? '#1f7a36' : '#b14040';
                                const isActualSet = actual != null;
                                return (
                                <tr key={r.id} style={r.is_overdue ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                    <td>
                                        <strong>{formatDate(r.due_date, locale)}</strong>
                                        {r.is_overdue ? <div style={{ fontSize: '0.74rem', color: '#b14040', fontWeight: 700 }}>⚠ overdue</div> : (
                                            r.days_to_due != null && !r.paid_at ? <div className="muted" style={{ fontSize: '0.72rem' }}>{r.days_to_due > 0 ? `za ${Math.abs(r.days_to_due)}d` : 'danas'}</div> : null
                                        )}
                                    </td>
                                    <td>🏭 {r.supplier_name}</td>
                                    <td className="muted" style={{ fontSize: '0.78rem' }}>{r.order_reference ?? '—'}<br /><span style={{ fontSize: '0.7rem' }}>commit: {formatDate(r.committed_at, locale)}</span></td>
                                    <td style={{ textAlign: 'right' }}>{sales != null ? formatMoney(sales, locale) : <span className="muted">—</span>}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(costEst, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {isActualSet ? (
                                            <span style={{ color: '#34383d', fontWeight: 700 }}>
                                                {formatMoney(actual!, locale)}
                                                {r.actual_supplier_invoice_ref ? <div className="muted" style={{ fontSize: '0.68rem' }}>📄 {r.actual_supplier_invoice_ref}</div> : null}
                                            </span>
                                        ) : (
                                            <button type="button" className="secondary compact" onClick={() => void setActualCost(r)} style={{ fontSize: '0.72rem', padding: '2px 8px' }}>+ {t('Unesi', 'Set')}</button>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: rucColor, whiteSpace: 'nowrap' }}>
                                        {ruc != null ? formatMoney(ruc, locale) : '—'}
                                        {isActualSet ? <div style={{ fontSize: '0.66rem', color: rucColor, opacity: 0.85 }}>{t('STVARNI', 'REAL')}</div> : ruc != null ? <div className="muted" style={{ fontSize: '0.66rem' }}>{t('očekivani', 'expected')}</div> : null}
                                    </td>
                                    <td>
                                        {r.paid_at ? (
                                            <span style={{ background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ✓ {t('Plaćeno', 'Paid')} {formatDate(r.paid_at, locale)}
                                                {r.paid_eur != null && r.paid_eur !== r.commitment_eur ? <span style={{ marginLeft: 4 }}>({formatMoney(r.paid_eur, locale)})</span> : null}
                                            </span>
                                        ) : r.is_overdue ? (
                                            <span style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ⚠ overdue
                                            </span>
                                        ) : (
                                            <span style={{ background: 'rgba(var(--ad-acc-rgb),0.15)', color: 'var(--ad-acc-3)', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ⏳ {t('Scheduled', 'Scheduled')}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button
                                            type="button"
                                            onClick={() => void downloadFile(r.id, 'pdf', r.supplier_name)}
                                            title={t('Preuzmi PDF narudžbe', 'Download order PDF')}
                                            style={{ background: 'transparent', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', color: 'var(--ad-ink)', fontSize: '0.72rem', padding: '2px 6px', marginRight: 3, cursor: 'pointer', borderRadius: 4 }}
                                        >📄</button>
                                        <button
                                            type="button"
                                            onClick={() => void downloadFile(r.id, 'xlsx', r.supplier_name)}
                                            title={t('Preuzmi XLSX s detaljima (artikli, vezane narudžbe, RUC)', 'Download XLSX with details (items, linked orders, RUC)')}
                                            style={{ background: 'transparent', border: '1px solid rgba(31,122,54,0.4)', color: '#1f7a36', fontSize: '0.72rem', padding: '2px 6px', marginRight: 6, cursor: 'pointer', borderRadius: 4 }}
                                        >📊</button>
                                        {!r.paid_at ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="secondary compact"
                                                    onClick={() => void sendPdf(r.id, r.supplier_name)}
                                                    title={r.pdf_sent_at ? t(`Već poslano ${formatDate(r.pdf_sent_at, locale)} na ${r.pdf_sent_to ?? ''}`, `Already sent ${formatDate(r.pdf_sent_at, locale)} to ${r.pdf_sent_to ?? ''}`) : t('Generiraj PDF nalog i pošalji dobavljaču', 'Generate PDF order and email supplier')}
                                                    style={{ fontSize: '0.72rem', padding: '2px 6px', marginRight: 4, opacity: r.pdf_sent_at ? 0.65 : 1 }}
                                                >
                                                    📧 {r.pdf_sent_at ? t('PDF ✓', 'PDF ✓') : t('PDF', 'PDF')}
                                                </button>
                                                <button type="button" className="secondary compact" onClick={() => void markProcessed(r.id)} title={t('Obrađeno (cijena fiksirana) — pita informirati hedging', 'Processed (price fixed) — asks to inform hedging')} style={{ fontSize: '0.72rem', padding: '2px 6px', marginRight: 4 }}>
                                                    🔒 {t('Obrađeno', 'Processed')}
                                                </button>
                                                <button type="button" className="compact" onClick={() => void markPaid(r.id, r.commitment_eur)} style={{ background: '#1f7a36', color: '#fff', fontSize: '0.72rem', padding: '2px 6px', marginRight: 4 }}>
                                                    ✓ {t('Plaćeno', 'Paid')}
                                                </button>
                                                <button type="button" className="secondary compact" onClick={() => void cancel(r.id, r.order_reference)} style={{ color: '#b14040', fontSize: '0.72rem', padding: '2px 6px' }}>
                                                    🗑
                                                </button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

/**
 * Forward Forecast — day-by-day projekcija likvidnosti.
 * Trenutno stanje + očekivani priljevi (IRA-e po due_date) - sigurni odljevi (Cash Schedule).
 */
export function CashflowForwardForecastTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [days, setDays] = useState(30);
    const [threshold, setThreshold] = useState('0');
    const [supplier, setSupplier] = useState('');
    const [suppliers, setSuppliers] = useState<string[]>([]);
    const [data, setData] = useState<ForecastResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Učitaj listu dobavljača (jednom)
    useEffect(() => {
        api<{ rows: Array<{ supplier_name: string }> }>(`/gold-cm-planner/cash-schedule?status=open`, token)
            .then((r) => {
                const uniq = Array.from(new Set(r.rows.map((x) => x.supplier_name))).sort();
                setSuppliers(uniq);
            })
            .catch(() => undefined);
    }, [token]);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const qs = new URLSearchParams();
            qs.set('days', String(days));
            qs.set('alert_threshold_eur', threshold || '0');
            if (supplier) qs.set('supplier', supplier);
            setData(await api<ForecastResponse>(`/gold-cm-planner/forecast?${qs.toString()}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, days, threshold, supplier]);

    useEffect(() => { void load(); }, [load]);

    if (loading && !data) return <p className="muted">{t('Računam…', 'Calculating…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    const minBal = Math.min(0, ...data.days.map((d) => d.ending_balance_eur));
    const maxBal = Math.max(...data.days.map((d) => d.ending_balance_eur), data.starting_balance_eur);
    const range = maxBal - minBal || 1;
    const W = 760;
    const H = 140;
    const points = data.days.map((d, i) => {
        const x = (i / Math.max(1, data.days.length - 1)) * W;
        const y = H - ((d.ending_balance_eur - minBal) / range) * H;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const polyline = points.join(' ');
    const zeroY = H - ((0 - minBal) / range) * H;

    return (
        <>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.84rem' }}>
                {t(
                    'Day-by-day projekcija likvidnosti. Trenutno stanje (iz bankovnih izvoda + opening balansi) + očekivani priljevi iz IRA-e (po due_date) - sigurni odljevi iz Cash Schedule.',
                    'Day-by-day liquidity projection. Current balance + expected inflows from outgoing invoices (by due_date) - certain outflows from Cash Schedule.',
                )}
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('Horizont', 'Horizon')}:
                    <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>
                        <option value="7">7d</option>
                        <option value="14">14d</option>
                        <option value="30">30d</option>
                        <option value="60">60d</option>
                        <option value="90">90d</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('Alarm threshold (EUR)', 'Alarm threshold (EUR)')}:
                    <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', width: 100 }} />
                </label>
                <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('Dobavljač', 'Supplier')}:
                    <select value={supplier} onChange={(e) => setSupplier(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>
                        <option value="">— {t('Svi (s priljevima IRA)', 'All (with IRA inflows)')} —</option>
                        {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
                    {t('Start', 'Start')}: {data.starting_balance_as_of ? formatDate(data.starting_balance_as_of, locale) : '—'}
                </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
                <SummaryMetric label={t('Trenutni saldo', 'Starting balance')} value={formatMoney(data.starting_balance_eur, locale)} accent={data.starting_balance_eur < 0 ? '#b14040' : 'var(--ad-ink)'} />
                <SummaryMetric label={t('Očekivani priljevi', 'Expected inflows')} value={formatMoney(data.totals.inflows_expected_eur, locale)} accent="#1f7a36" />
                <SummaryMetric label={t('Sigurni odljevi', 'Certain outflows')} value={formatMoney(data.totals.outflows_scheduled_eur, locale)} accent="var(--ad-acc-3)" />
                <SummaryMetric label={t('Najniži saldo', 'Lowest balance')} value={formatMoney(data.totals.lowest_balance_eur, locale)} accent={data.totals.lowest_balance_eur < 0 ? '#b14040' : '#1f7a36'} />
            </div>

            {data.alerts.length > 0 ? (
                <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                    <strong style={{ color: '#b14040', fontSize: '0.86rem' }}>⚠ {data.alerts.length} {t('alarma likvidnosti', 'liquidity alerts')}</strong>
                    <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: '0.78rem', color: '#34383d' }}>
                        {data.alerts.slice(0, 5).map((a, i) => (
                            <li key={i}>{formatDate(a.date, locale)}: {a.message}</li>
                        ))}
                        {data.alerts.length > 5 ? <li className="muted">… +{data.alerts.length - 5}</li> : null}
                    </ul>
                </div>
            ) : null}

            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: 12, marginBottom: 14, overflow: 'hidden' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                    {t('Stacked area + linija salda', 'Stacked area + balance line')}
                </h3>
                <StackedAreaChart
                    locale={locale}
                    height={220}
                    xLabels={data.days.map((d) => d.date.slice(5))}
                    moneyFormatter={(n) => formatMoney(n, locale)}
                    series={[
                        { name: t('Otvaranje', 'Opening'), color: '#e8d8a2', values: data.days.map((d) => Math.max(0, d.opening_balance_eur)) },
                        { name: t('Priljevi', 'Inflows'), color: '#1f7a36', values: data.days.map((d) => d.inflows_expected_eur) },
                    ]}
                    overlay={{
                        name: t('Zatvarajući saldo', 'Ending balance'),
                        color: 'var(--ad-acc)',
                        values: data.days.map((d) => d.ending_balance_eur),
                    }}
                />
                <p className="muted" style={{ fontSize: '0.72rem', marginTop: 6, fontStyle: 'italic' }}>
                    {t('Zlatna linija = završni saldo (uključuje odljeve). Zeleni area = priljevi tog dana iznad opening salda.', 'Gold line = ending balance (after outflows). Green area = inflows on top of opening balance.')}
                </p>
            </div>

            <details style={{ marginBottom: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.78rem', color: 'var(--ad-ink)', fontWeight: 600 }}>
                    📈 {t('Stara jednostavna linija salda', 'Old simple balance line')}
                </summary>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: 12, marginTop: 8, overflow: 'hidden' }}>
                    <svg viewBox={`0 0 ${W} ${H + 16}`} style={{ width: '100%', height: 160 }}>
                        {zeroY >= 0 && zeroY <= H ? (
                            <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="rgba(177,64,64,0.4)" strokeDasharray="4 4" />
                        ) : null}
                        <polyline points={polyline} fill="none" stroke="#c99b31" strokeWidth="2" />
                        {data.days.map((d, i) => {
                            if (!d.alert) return null;
                            const x = (i / Math.max(1, data.days.length - 1)) * W;
                            const y = H - ((d.ending_balance_eur - minBal) / range) * H;
                            return <circle key={i} cx={x} cy={y} r="3" fill={d.alert === 'negative' ? '#b14040' : 'var(--ad-acc)'} />;
                        })}
                    </svg>
                </div>
            </details>

            <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.84rem', color: 'var(--ad-ink)', marginBottom: 8, fontWeight: 600 }}>
                    📅 {t('Detaljno po danima', 'Day-by-day detail')}
                </summary>
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Datum', 'Date')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Otvaranje', 'Opening')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Priljev (oček.)', 'Inflow (exp.)')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Odljev (sig.)', 'Outflow (cert.)')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Zatvaranje', 'Ending')}</th>
                                <th>{t('Alarm', 'Alert')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.days.map((d, i) => (
                                <tr key={i} style={d.alert ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                    <td>{formatDate(d.date, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(d.opening_balance_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', color: d.inflows_expected_eur > 0 ? '#1f7a36' : '#999' }}>{d.inflows_expected_eur > 0 ? '+' + formatMoney(d.inflows_expected_eur, locale) : '—'}</td>
                                    <td style={{ textAlign: 'right', color: d.outflows_scheduled_eur > 0 ? '#b14040' : '#999' }}>{d.outflows_scheduled_eur > 0 ? '-' + formatMoney(d.outflows_scheduled_eur, locale) : '—'}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: d.ending_balance_eur < 0 ? '#b14040' : '#34383d' }}>{formatMoney(d.ending_balance_eur, locale)}</td>
                                    <td>{d.alert === 'negative' ? <span style={{ color: '#b14040', fontWeight: 700 }}>⚠ negativan</span> : (d.alert === 'low' ? <span style={{ color: 'var(--ad-acc-3)' }}>⚠ nizak</span> : '')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </>
    );
}

function SummaryMetric({ label, value, accent }: { label: string; value: string; accent: string }) {
    return (
        <div style={{
            background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px',
        }}>
            <div style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
        </div>
    );
}

/* ── Tab URA dospijeća ─────────────────────────────────────────── */

type UraRow = {
    id: number;
    document_reference: string;
    partner_name: string | null;
    document_date: string | null;
    amount_eur: number;
    manual_due_date: string | null;
    manual_paid_at: string | null;
    manual_notes: string | null;
    company_code: string | null;
    company_name: string | null;
    is_overdue: boolean;
};

type UraResponse = {
    rows: UraRow[];
    summary: {
        total_unscheduled_eur: number;
        total_scheduled_eur: number;
        total_overdue_eur: number;
    };
};

export function UraScheduleTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<UraResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<'open' | 'unscheduled' | 'scheduled' | 'paid'>('unscheduled');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setData(await api<UraResponse>(`/ura-schedule?status=${statusFilter}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => { void load(); }, [load]);

    async function setDue(id: number, currentDue: string | null) {
        const v = window.prompt(t('Dospijeće (yyyy-mm-dd ili prazno za reset):', 'Due date (yyyy-mm-dd or empty to reset):'), currentDue ?? '');
        if (v === null) return;
        try {
            await mutate(`/ura-schedule/${id}/due`, token, 'PATCH', { manual_due_date: v || null });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function markPaid(id: number) {
        if (!confirm(t('Označiti kao plaćeno danas?', 'Mark as paid today?'))) return;
        try {
            await mutate(`/ura-schedule/${id}/mark-paid`, token, 'POST', {});
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0 0 12px' }}>
                {t(
                    'URA-e (ulazni računi) nemaju due_date u shemi. Ovdje postavi manual due_date za URA-e koje žeš da ulaze u Forward Forecast (struja, najamnine, plaće, dobavljači mimo Gold CM Plannera).',
                    'Incoming invoices have no due_date in schema. Set manual_due_date here for invoices that should enter Forward Forecast (utilities, rent, payroll, suppliers outside Gold CM Planner).',
                )}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
                <SummaryMetric label={t('Bez dospijeća', 'Unscheduled')} value={formatMoney(data.summary.total_unscheduled_eur, locale)} accent={data.summary.total_unscheduled_eur > 0 ? 'var(--ad-acc-3)' : '#888'} />
                <SummaryMetric label={t('S dospijećem (forecast in)', 'Scheduled (forecast in)')} value={formatMoney(data.summary.total_scheduled_eur, locale)} accent="var(--ad-ink)" />
                <SummaryMetric label={t('Overdue', 'Overdue')} value={formatMoney(data.summary.total_overdue_eur, locale)} accent={data.summary.total_overdue_eur > 0 ? '#b14040' : '#1f7a36'} />
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {(['unscheduled', 'scheduled', 'open', 'paid'] as const).map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setStatusFilter(s)}
                        style={{
                            padding: '4px 10px', borderRadius: 6,
                            border: statusFilter === s ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            background: statusFilter === s ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                            color: statusFilter === s ? '#fffbeb' : 'var(--ad-ink)',
                            fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        {s === 'unscheduled' ? t('Bez datuma', 'No date')
                            : s === 'scheduled' ? t('Zakazano', 'Scheduled')
                            : s === 'open' ? t('Sve otvorene', 'All open')
                            : t('Plaćeno', 'Paid')}
                    </button>
                ))}
            </div>

            {data.rows.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Nema URA-a s ovim statusom.', 'No URA with this status.')}
                </p>
            ) : (
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Datum URA', 'Doc date')}</th>
                                <th>{t('Referenca', 'Reference')}</th>
                                <th>{t('Dobavljač', 'Partner')}</th>
                                <th>{t('Firma', 'Co.')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                <th>{t('Dospijeće', 'Due')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.id} style={r.is_overdue ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                    <td>{r.document_date ? formatDate(r.document_date, locale) : '—'}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{r.document_reference}</td>
                                    <td>{r.partner_name ?? '—'}</td>
                                    <td>{r.company_code ?? '—'}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.amount_eur, locale)}</td>
                                    <td>
                                        {r.manual_paid_at ? (
                                            <span style={{ color: '#1f7a36', fontWeight: 700 }}>✓ {formatDate(r.manual_paid_at, locale)}</span>
                                        ) : r.manual_due_date ? (
                                            <span style={{ color: r.is_overdue ? '#b14040' : 'var(--ad-ink)', fontWeight: 600 }}>
                                                {formatDate(r.manual_due_date, locale)}{r.is_overdue ? ' ⚠' : ''}
                                            </span>
                                        ) : (
                                            <span className="muted" style={{ fontStyle: 'italic' }}>{t('nije zakazano', 'not scheduled')}</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {!r.manual_paid_at ? (
                                            <>
                                                <button type="button" className="secondary compact" onClick={() => void setDue(r.id, r.manual_due_date)} style={{ fontSize: '0.74rem', padding: '3px 8px', marginRight: 4 }}>
                                                    📅 {t('Dospijeće', 'Due')}
                                                </button>
                                                {r.manual_due_date ? (
                                                    <button type="button" className="compact" onClick={() => void markPaid(r.id)} style={{ background: '#1f7a36', color: '#fff', fontSize: '0.74rem', padding: '3px 8px' }}>
                                                        ✓ {t('Plaćeno', 'Paid')}
                                                    </button>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
